import React from "react";

// ONE component, ONE map for every state-driven status chip in the app. Solid fills, dark/light
// text per fill, square corners, no outline, no glow. JetBrains Mono 700 / 9.5px / .15em /
// uppercase / 5px 10px. No component styles a status chip inline — they all resolve here.
// Recognition badges (FOUNDING MEMBER, AMBASSADOR, BETA TESTER) are a SEPARATE family
// (Badges.jsx) and are deliberately NOT handled here.
export const STATUS_CHIP = {
  // GOOD — shared token --status-good
  active:          { label: "ACTIVE",          bg: "#3A4A1E", text: "#CFE0A4" },
  email_verified:  { label: "EMAIL VERIFIED",  bg: "#3A4A1E", text: "#CFE0A4" },
  id_verified:     { label: "ID VERIFIED",     bg: "#3A4A1E", text: "#CFE0A4" },
  // DEALER — its own blue family
  dealer_verified: { label: "DEALER VERIFIED", bg: "#1F6BA8", text: "#04121F" },
  // PROBLEM
  not_started:     { label: "NOT STARTED",     bg: "#2A2B2D", text: "#9A9DA1" },
  pending:         { label: "PENDING",         bg: "#7A5A12", text: "#1A1303" },
  locked:          { label: "LOCKED",          bg: "#8A4A10", text: "#180B01" }, // count appended below
  failed:          { label: "FAILED",          bg: "#7C2C1C", text: "#F0C4B8" },
  revoked:         { label: "REVOKED",         bg: "#A33D28", text: "#1B0703" },
  // LIFECYCLE
  on_hold:         { label: "ON HOLD",         bg: "#3A3116", text: "#E2C77C" },
  disabled:        { label: "DISABLED",        bg: "#2A2B2D", text: "#A0A3A7" },
  archived:        { label: "ARCHIVED",        bg: "#1E1F21", text: "#7C7F83" },
  // PAYOUTS (Stripe Connect) — the 4-state seller payout chip system.
  enabled:         { label: "ENABLED",         bg: "#3A4A1E", text: "#CFE0A4" },
  in_progress:     { label: "IN PROGRESS",     bg: "#7A5A12", text: "#1A1303" },
  restricted:      { label: "RESTRICTED",      bg: "#A33D28", text: "#1B0703" },
  // ADMIN ROSTER payout column (Stripe Connect, webhook-synced)
  payout_connected:     { label: "CONNECTED",     bg: "#3A4A1E", text: "#CFE0A4" },
  payout_action_needed: { label: "ACTION NEEDED", bg: "#7A5A12", text: "#1A1303" },
  payout_not_connected: { label: "NOT CONNECTED", bg: "#2A2B2D", text: "#9A9DA1" },
  // ID-VERIFICATION TAXONOMY (does the flag have real Stripe backing?)
  id_backed:       { label: "STRIPE VERIFIED", bg: "#3A4A1E", text: "#CFE0A4" },
  id_exempt:       { label: "EXEMPT",          bg: "#2A2B2D", text: "#9A9DA1" }, // gray, never "verified"
  id_unbacked:     { label: "UNBACKED",        bg: "#7C2C1C", text: "#F0C4B8" }, // loud — flag with no backing
};

// Map the taxonomy bucket (verified | exempt | unbacked) from the backend to its chip token.
// Returns null for demo_test / unknown so callers can choose not to render a chip.
export function idTaxonomyChip(taxonomy) {
  return { verified: "id_backed", exempt: "id_exempt", unbacked: "id_unbacked" }[taxonomy] || null;
}

// Unmapped states render a gray UNKNOWN — loudly wrong so it gets noticed and fixed,
// never silently inheriting a neighbouring style.
export const STATUS_UNKNOWN = { label: "UNKNOWN", bg: "#2A2B2D", text: "#9A9DA1" };

export function StatusChip({ state, count = null, className = "", testId }) {
  const meta = STATUS_CHIP[state] || STATUS_UNKNOWN;
  const resolved = STATUS_CHIP[state] ? state : "unknown";
  let label = meta.label;
  if (state === "locked" && count != null) label = `LOCKED ${count}/3`;
  return (
    <span
      data-testid={testId || `status-chip-${resolved}`}
      style={{
        backgroundColor: meta.bg, color: meta.text,
        fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: "9.5px",
        letterSpacing: "0.15em", padding: "5px 10px", lineHeight: 1, borderRadius: 0,
      }}
      className={`inline-flex items-center uppercase whitespace-nowrap ${className}`}
    >
      {label}
    </span>
  );
}

// Resolve the ID-verification chip token (+ attempt count for LOCKED) from a user/detail record.
// Works from a full admin `detail` (identity_verified / identity_verification.status /
// identity_attempts / identity_locked) OR a roster row that only carries `verification_state`.
export function idChipState(u = {}) {
  if (u.identity_verified === true) return { state: "id_verified" };
  const attempts = Number(u.identity_attempts || 0);
  if (u.identity_locked || attempts >= 3) return { state: "locked", count: Math.min(attempts || 3, 3) };
  const status = ((u.identity_verification || {}).status || "").toLowerCase();
  const six = u.verification_state || (
    u.reverify_required || status === "revoked" ? "revoked"
      : status === "requires_input" ? "failed"
      : ["processing", "requires_action", "pending"].includes(status) ? "in_progress"
      : (attempts > 0 || ["canceled", "cancelled"].includes(status)) ? "abandoned"
      : "unverified"
  );
  // Six-state canonical model → chip token. `abandoned` and `in_progress` both read PENDING
  // (mid-funnel, incomplete); `unverified` reads NOT STARTED.
  const MAP = {
    verified: "id_verified", revoked: "revoked", failed: "failed",
    in_progress: "pending", abandoned: "pending", unverified: "not_started",
  };
  return { state: MAP[six] || "not_started" };
}
